import { TranscriptionService } from '@klangio/ai2notes-ts-client';
import type { TFunction } from 'i18next';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TranscriptionState } from '../entities';
import type { TranscriptionFinishData, TranscriptionProgressData } from '../services';

const getMessageByStateAndQueuePosition = (
  state: string,
  queuePosition: number,
  translations: TFunction,
) => {
  let message = translations('home.input.processing');
  if (state === TranscriptionState.Enqueued) {
    if (queuePosition) {
      message = `${translations('home.input.in_queue')}, ${translations(
        'home.input.position',
      )} ${queuePosition}`;
    } else {
      message = translations('home.input.in_queue');
    }
  }
  return message;
};

const updateTranscription = (
  translations: TFunction,
  jobId: string,
  onUpdateReceived: (data: TranscriptionProgressData) => void,
  onFinished: (finish_data: TranscriptionFinishData) => void,
  intervalId: ReturnType<typeof setInterval | typeof setTimeout>,
) => {
  return TranscriptionService.transcriptionGetGet(jobId)
    .then((e) => {
      const updateData: TranscriptionProgressData = {
        progress: 0,
        jobState: TranscriptionState.Enqueued,
        message: '',
      };
      updateData.progress = e.progress;
      updateData.jobState = e.state;
      updateData.message = getMessageByStateAndQueuePosition(
        e.state,
        e.queuePosition,
        translations,
      );

      if (e.state === TranscriptionState.Ok) {
        clearInterval(intervalId);
        onFinished({
          state: e.state,
          id: e.id,
        });
      } else if (e.state === TranscriptionState.Error || e.state === TranscriptionState.Refunded) {
        clearInterval(intervalId);
        onFinished({
          state: e.state,
          id: e.id,
        });
      }
      onUpdateReceived(updateData);
    })
    .catch((e) => {
      console.log('updateTranscription error', e);
    });
};

const useProcessingTranscriptionUpdate = (
  jobId: string,
  onUpdateReceived: (data: TranscriptionProgressData) => void,
  onFinished: (finish_data: TranscriptionFinishData) => void,
) => {
  const [count, setCount] = useState(0);
  const [isEnabled, setIsEnabled] = useState(false);
  const { t } = useTranslation();

  const startUpdating = () => {
    setIsEnabled(true);
  };

  const stopUpdating = () => {
    setIsEnabled(false);
  };

  const handleOnFinished = useCallback(
    (data: TranscriptionFinishData) => {
      setIsEnabled(false);
      onFinished(data);
    },
    [onFinished],
  );

  useEffect(() => {
    let intervalId: ReturnType<typeof setInterval> | null = null;
    if (isEnabled) {
      intervalId = setInterval(() => {
        setCount((prevCount: number) => prevCount + 1);
        updateTranscription(t, jobId, onUpdateReceived, handleOnFinished, intervalId);
      }, 1000);
    }
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
        intervalId = null;
      }
    };
  }, [isEnabled]);

  return {
    startUpdating,
    stopUpdating,
    count,
  };
};

export default useProcessingTranscriptionUpdate;
